import moment from 'moment';

// This function outputs times for our time selector.
//
// Objects have this form:
// ...
// {
//   title: '8:30 pm',    (or '20:30', according to locale)
//   key:   '20:30',
// },
// ...
export const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';
export const DEFAULT_DATE_FORMAT_IN_WORDS = 'ddd, MMM D, YYYY';
export const DEFAULT_TIME_FORMAT = 'H:mm';
export const ENGLISH_DEFAULT_TIME_FORMAT = 'h:mm A';

const times = locale =>
  [...Array(24)]
    .map((hourValue, hour) =>
      [...Array(60)].map((minuteValue, minute) => ({
        title: moment()
          .hour(hour)
          .minute(minute)
          .locale(locale)
          .format('LT'),
        key: moment()
          .hour(hour)
          .minute(minute)
          .locale(locale)
          .format('HH:mm'),
      })),
    )
    .reduce((acc, value) => acc.concat(value), []);

// This function takes the values above
// and filters them according to user input, filtering spaces
// and adjusting granularity (half-hours or to the minute times).

const getResults = (value, locale = 'en', opts = { isToday: false, onlyShowTimesAfterGivenTime: '' }) => {
  const toSearch = value.toUpperCase().replace(/\s/g, '');
  const filterTimes = time => {
    // always: accept times that end in '00' or '30'
    // if: user selects today's date; only accept times after the current time
    const timeEndsWithZeroOrThirty = key => key.endsWith('00') || key.endsWith('30');
    const timeIsAfterCurrentTime = key => moment(key, 'H:mm') > moment();
    const timeIsAfterGivenTime = (key, givenKey) => moment(key, 'H:mm') > moment(givenKey, 'H:mm');

    if (opts.onlyShowTimesAfterGivenTime) {
      return timeEndsWithZeroOrThirty(time.key) && timeIsAfterGivenTime(time.key, opts.onlyShowTimesAfterGivenTime);
    } else if (opts.isToday) return timeEndsWithZeroOrThirty(time.key) && timeIsAfterCurrentTime(time.key);
    return timeEndsWithZeroOrThirty(time.key);
  };

  const collection =
    !value || !toSearch.match(/\d{1,2}:\d/) ? times(locale).filter(time => filterTimes(time)) : times(locale);

  return value ? collection.filter(time => time.title.replace(/\s/g, '').startsWith(toSearch)) : collection;
};

const addEndDatePrediction = (time, locale = 'en') => {
  if (time.startDate && time.startTime && !time.endDate && !time.endTime) {
    const start = moment(`${time.startDate.format('DD/MM/YYYY')}-${time.startTime.key}`, 'DD/MM/YYYY-hh:mm');

    const predictedEnd = start.add(3, 'hours');
    const newTime = time;
    newTime.endTime = {
      title: predictedEnd.locale(locale).format('LT'),
      key: predictedEnd.locale(locale).format('HH:mm'),
    };
    newTime.endDate = predictedEnd.startOf('day');
    return newTime;
  }
  return time;
};

const isDateSelectedToday = date => moment(date).isSame(moment(), 'day');

const parseEventTime = dateTimeString => {
  // input: '2018-07-09T14:24:00' (filled) or '2018-07-09'/'T14:24:00' (partial) or undefined (empty)
  // output: { key: "14:00", title: "2:00 PM" } or null

  if (typeof dateTimeString !== 'string') return null;
  const time = moment(dateTimeString.split('T')[1], 'HH:mm:ss');

  if (time.isValid()) {
    return {
      key: time.format('HH:mm'),
      title: time.format('LT'),
    };
  }
  return null;
};

const parseEventDate = dateTimeString => {
  // input: '2018-07-09T14:24:00' (filled) or '2018-07-09'/'T14:24:00' (partial) or undefined (empty)
  // output: moment object or null

  if (typeof dateTimeString !== 'string') return null;
  const date = moment(dateTimeString.split('T')[0], 'YYYY-MM-DD');

  if (date.isValid()) return date;
  return null;
};

// Returns a string like 'Thu, Oct 25 10:30 AM EST'
export const displayDateAndTimeWithTimeZone = (date, time, timezone) =>
  moment.tz(`${date.format('YYYY-MM-DD')} ${time.key}`, timezone).format('MMM D, YYYY h:mmA z');

export function getTimeFormat(locale) {
  return locale === 'en' ? ENGLISH_DEFAULT_TIME_FORMAT : DEFAULT_TIME_FORMAT;
}

const getDateString = (dateTime, locale, timeFormat) => moment(dateTime).locale(locale).format(timeFormat);

// Returns a string like 'Oct 25, 2023, 10:30 AM'
export function displayDateTimeString(dateTime, locale) {
  const timeFormat = getTimeFormat(locale);

  return getDateString(dateTime, locale, `MMM D, YYYY, ${timeFormat}`);
}

// Returns a string like 'Oct 25, 2023'
export function displayDateString(dateTime, locale) {
  // return moment(dateTime).locale(locale).format(`MMM D, YYYY`);
  return getDateString(dateTime, locale, 'MMM D, YYYY');
}

// Returns a string like '10:30 AM'
export function displayTimeString(dateTime, locale) {
  const timeFormat = getTimeFormat(locale);

  return getDateString(dateTime, locale, `${timeFormat}`);
}

export function buildMomentInTimezone(dateStr, timeStr, timezone) {
  const [hours, minutes] = timeStr.split(':').map(Number);

  return moment.tz(dateStr, timezone)
    .hour(hours)
    .minute(minutes)
    .second(0)
    .millisecond(0);
}

const defaultTimezone = 'GMT';

export default {
  times,
  getResults,
  addEndDatePrediction,
  isDateSelectedToday,
  parseEventTime,
  parseEventDate,
  defaultTimezone,
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_FORMAT_IN_WORDS,
  DEFAULT_TIME_FORMAT,
  ENGLISH_DEFAULT_TIME_FORMAT
};
